/**
 * Where a saved view lives, as a link that opens it — the "My views" panel's
 * way to any view from anywhere in the module.
 *
 * A view belongs to one chart on one point (or, for Capacity and RECs, one
 * region), so its link is that chart's url with two params:
 *
 * - `view` — the view to apply. `useMarketView` reads it on arrival, applies
 *   the view, and drops the param.
 * - `strip` — for a Strips view, which strip it belongs to. The mode is the
 *   one piece of chart state not inside a view (`StripsView` keeps it per
 *   point), so the link has to say it; `StripsView` reads and drops it.
 *
 * A link can also open a chart on a set of filters that is no saved view
 * (`filtersHref`, `?filters=`): how the Daily Briefing's cards open their
 * figures in Forwards or Index.
 */

import { DEFAULT_CONFIG, configToStored, type MarketViewConfig } from "./view-config";
import { pointPath, regionPath } from "./paths";

import type { MarketHub, StripMode } from "@/types/market-intelligence";
import type { MarketViewScope } from "@/types/market-views";

export const VIEW_PARAM = "view";
export const STRIP_PARAM = "strip";
export const FILTERS_PARAM = "filters";

/** What each chart is called where a view is listed away from it. */
export const SCOPE_LABELS: Record<MarketViewScope, string> = {
  forwards_strips_rolling: "Rolling strips",
  forwards_strips_calendar: "Calendar strips",
  forwards_strips_quarterly: "Quarterly strips",
  forwards_curve: "Forward curve",
  index_trends: "Index trends",
  index_hourly: "Hourly",
  index_basis: "Basis",
  region_capacity: "Capacity",
  region_recs: "RECs",
};

/** The strip a Strips scope saves views for. */
const STRIP_MODE_BY_SCOPE: Partial<Record<MarketViewScope, StripMode>> = {
  forwards_strips_rolling: "rolling",
  forwards_strips_calendar: "calendar",
  forwards_strips_quarterly: "quarterly",
};

/** Capacity and RECs save views per region (`hub_code` is its slug), not per point. */
export function isRegionScope(scope: MarketViewScope): boolean {
  return scope === "region_capacity" || scope === "region_recs";
}

/** Whether a param value names a strip, for `StripsView` reading `?strip=`. */
export function parseStripMode(value: string | null): StripMode | null {
  return value === "rolling" || value === "calendar" || value === "quarterly" ? value : null;
}

/**
 * The url that opens a view: its chart, on its point or region, with the view
 * applied. `null` for a point view whose point the topology no longer has —
 * there is nowhere to send the reader.
 */
export function viewHref(
  view: { id: string; scope: MarketViewScope; hub_code: string },
  hub: Pick<MarketHub, "commodity" | "iso" | "code"> | undefined,
): string | null {
  const params = new URLSearchParams();
  const mode = STRIP_MODE_BY_SCOPE[view.scope];
  if (mode) params.set(STRIP_PARAM, mode);
  params.set(VIEW_PARAM, view.id);
  const query = `?${params.toString()}`;

  if (view.scope === "region_capacity") return `${regionPath(view.hub_code, "capacity")}${query}`;
  if (view.scope === "region_recs") return `${regionPath(view.hub_code, "recs")}${query}`;
  if (!hub) return null;
  const path = chartPath(view.scope, hub);
  return path && `${path}${query}`;
}

/** A point's chart, by its scope; `null` for a region's. */
function chartPath(scope: MarketViewScope, hub: Pick<MarketHub, "commodity" | "iso" | "code">): string | null {
  switch (scope) {
    case "forwards_strips_rolling":
    case "forwards_strips_calendar":
    case "forwards_strips_quarterly":
      return `${pointPath(hub, "forwards")}/strips`;
    case "forwards_curve":
      return `${pointPath(hub, "forwards")}/curve`;
    case "index_trends":
      return `${pointPath(hub, "index")}/trends`;
    case "index_hourly":
      return `${pointPath(hub, "index")}/hourly`;
    case "index_basis":
      return `${pointPath(hub, "index")}/basis`;
    default:
      return null;
  }
}

/**
 * The url that opens a point's chart on a set of filters: the fields given,
 * the rest at their defaults, as a selection of the reader's own ("Custom")
 * rather than a saved view. The Daily Briefing's cards open their figures
 * this way.
 *
 * The filters ride in `?filters=` as the chart's scope and its stored config
 * (`configToStored`), and `useMarketView` reads them through the codec a
 * saved view goes through (`configFromStored`), so an old or hand-edited link
 * degrades a field at a time. A Strips link names its strip too, as a saved
 * view's does. `null` for a region's chart.
 */
export function filtersHref(
  hub: Pick<MarketHub, "commodity" | "iso" | "code">,
  scope: MarketViewScope,
  filters: Partial<MarketViewConfig>,
): string | null {
  const path = chartPath(scope, hub);
  if (!path) return null;
  const params = new URLSearchParams();
  const mode = STRIP_MODE_BY_SCOPE[scope];
  if (mode) params.set(STRIP_PARAM, mode);
  params.set(FILTERS_PARAM, JSON.stringify({ scope, config: configToStored(scope, { ...DEFAULT_CONFIG, ...filters }) }));
  return `${path}?${params.toString()}`;
}

/** A `?filters=` value, read: the chart it is for and its stored config, or `null` for anything else. */
export function parseLinkedFilters(
  value: string | null,
): { scope: MarketViewScope; stored: Record<string, unknown> } | null {
  if (!value) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const { scope, config } = parsed as { scope?: unknown; config?: unknown };
  if (typeof scope !== "string" || !Object.keys(SCOPE_LABELS).includes(scope)) return null;
  if (!config || typeof config !== "object" || Array.isArray(config)) return null;
  return { scope: scope as MarketViewScope, stored: config as Record<string, unknown> };
}
